import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, DoorOpen, Gamepad2 } from 'lucide-react';
import api from '../services/api';
import { useStore } from '../stores/useStore';
import RpgCityCanvas from '../components/rpg/RpgCityCanvas';

function isEditableTarget(target) {
  if (!target) return false;
  const tag = String(target.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable;
}

function getTileInteraction(tile) {
  const interaction = tile?.metadata?.interaction;
  if (!interaction || typeof interaction !== 'object' || interaction.enabled === false) return null;
  return {
    type: String(interaction.type || 'note').trim() || 'note',
    label: String(interaction.label || '').trim(),
    description: String(interaction.description || '').trim(),
    target: String(interaction.target || '').trim(),
    access_from: String(interaction.access_from || 'any').trim() || 'any'
  };
}

function getRelativeDirection(dx, dy) {
  if (dx === 0 && dy === 0) return 'center';
  if (dx === 0 && dy === -1) return 'north';
  if (dx === 0 && dy === 1) return 'south';
  if (dx === 1 && dy === 0) return 'east';
  if (dx === -1 && dy === 0) return 'west';
  return null;
}

function matchesAccessFrom(accessFrom, relation) {
  const normalizedAccess = String(accessFrom || 'any').trim() || 'any';
  if (normalizedAccess === 'any') return true;
  if (normalizedAccess === 'center') return relation === 'center';
  return normalizedAccess === relation;
}

function resolveImageSrc(image) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('blob:') || image.startsWith('/')) return image;
  const apiBase = import.meta.env.VITE_API_URL || '';
  return `${apiBase}/uploads/gallery/${image}`;
}

function buildInteriorScene(baseScene, interiorKey) {
  const tilesets = Array.isArray(baseScene?.tilesets) ? baseScene.tilesets : [];
  const enabledTilesets = tilesets.filter((entry) => entry?.is_enabled !== false && entry?.tileset);
  const findTileset = (roles) => enabledTilesets.find((entry) => roles.includes(String(entry.role || '').trim())) || enabledTilesets[0] || null;
  const terrainTileset = findTileset(['terrain', 'props', 'objects']);
  const buildingTileset = findTileset(['buildings', 'objects', 'props']) || terrainTileset;
  const floorIndex = 0;
  const wallIndex = 0;
  const doorIndex = 0;

  const width = 16;
  const height = 12;
  const tiles = [];
  const pushTile = (coordinate, layer, tileIndex, tileset, blocked = false, extraMetadata = {}) => {
    if (!tileset) return;
    tiles.push({
      coordinate,
      layer,
      tileset_role: String(tileset.role || 'terrain'),
      tile_index: Number(tileIndex) || 0,
      blocked: blocked ? 1 : 0,
      metadata: {
        tileset_id: Number(tileset.tileset_id || tileset.id || 0),
        tileset_role: String(tileset.role || 'terrain'),
        tileset_assignment_key: String(tileset.assignment_key || ''),
        ...extraMetadata
      }
    });
  };

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      pushTile((y * width) + x, 0, floorIndex, terrainTileset, false);
      const border = x === 0 || y === 0 || x === width - 1 || y === height - 1;
      if (border) {
        const isDoorSpot = x === Math.floor(width / 2) && y === height - 1;
        if (!isDoorSpot) {
          pushTile((y * width) + x, 1, wallIndex, buildingTileset, true);
        }
      }
    }
  }

  const doorCoordinate = ((height - 1) * width) + Math.floor(width / 2);
  pushTile(doorCoordinate, 1, doorIndex, buildingTileset, false, {
    interaction: {
      enabled: true,
      type: 'portal',
      label: 'Salir',
      description: 'Volver a la Cúpula.',
      target: '/rpg',
      access_from: 'any'
    }
  });

  const centerX = Math.floor(width / 2);
  const centerY = Math.floor(height / 2);
  if (buildingTileset) {
    pushTile((centerY * width) + (centerX - 2), 1, wallIndex, buildingTileset, false);
    pushTile((centerY * width) + (centerX - 1), 1, wallIndex, buildingTileset, false);
    pushTile((centerY * width) + centerX, 1, wallIndex, buildingTileset, false);
  }

  return {
    key: interiorKey,
    name: interiorKey.replace(/[-_]+/g, ' ').trim() || 'Interior',
    width,
    height,
    spawn: {
      x: Math.floor(width / 2),
      y: Math.max(1, height - 2),
      coordinate: (Math.max(1, height - 2) * width) + Math.floor(width / 2)
    },
    tilesets,
    tiles
  };
}

export default function RpgInterior() {
  const navigate = useNavigate();
  const { sceneKey: rawSceneKey } = useParams();
  const sceneKey = String(rawSceneKey || 'default').trim() || 'default';
  const { setRobot } = useStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [scene, setScene] = useState(null);
  const [player, setPlayer] = useState(null);
  const [playerVisual, setPlayerVisual] = useState(null);
  const [movementStatus, setMovementStatus] = useState('ready');
  const [interactionMessage, setInteractionMessage] = useState('');
  const moveAnimationRef = useRef(null);
  const movingRef = useRef(false);
  const playerRef = useRef(null);
  const heldDirectionRef = useRef(null);

  const clearSceneState = useCallback(async () => {
    try {
      await api.post('/robots/my-city-rpg-context/scene', { scene_key: '' });
    } catch (_error) {
      // Keep the local exit usable even if the save step fails.
    }
  }, []);

  const loadRpgContext = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [contextRes, appearanceRes] = await Promise.all([
        api.get(`/robots/my-city-rpg-context?t=${Date.now()}`),
        api.get(`/robots/rpg-appearance?t=${Date.now()}`)
      ]);
      const nextContext = contextRes.data;
      const latestAppearance = appearanceRes.data?.appearance || null;
      setContext(nextContext);
      if (nextContext.bot) setRobot(nextContext.bot);
      const generatedScene = buildInteriorScene(nextContext.rpg?.scene || null, sceneKey);
      setScene(generatedScene);
      setPlayer({
        x: generatedScene.spawn.x,
        y: generatedScene.spawn.y,
        facing: 'north',
        appearance: latestAppearance || nextContext.rpg?.player?.appearance || null
      });
      setMovementStatus('ready');
      setInteractionMessage('');
      await api.post('/robots/my-city-rpg-context/scene', {
        scene_key: `interior:${sceneKey}`,
        scene_label: generatedScene.name,
        return_x: Number(nextContext.rpg?.player?.x ?? generatedScene.spawn.x),
        return_y: Number(nextContext.rpg?.player?.y ?? generatedScene.spawn.y)
      });
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el interior RPG');
    } finally {
      setLoading(false);
    }
  }, [sceneKey, setRobot]);

  const stopMoveAnimation = useCallback(() => {
    if (moveAnimationRef.current) {
      window.cancelAnimationFrame(moveAnimationRef.current);
      moveAnimationRef.current = null;
    }
    movingRef.current = false;
    setPlayerVisual(null);
  }, []);

  useEffect(() => {
    playerRef.current = player;
  }, [player]);

  useEffect(() => {
    void loadRpgContext();
    return () => {
      void clearSceneState();
    };
  }, [clearSceneState, loadRpgContext]);

  const blockedSet = useMemo(() => {
    const set = new Set();
    (scene?.tiles || []).forEach((tile) => {
      if (Number(tile.blocked) === 1) {
        set.add(Number(tile.coordinate));
      }
    });
    return set;
  }, [scene?.tiles]);

  const interactiveTileMap = useMemo(() => {
    const map = new Map();
    (scene?.tiles || []).forEach((tile) => {
      const interaction = getTileInteraction(tile);
      if (!interaction) return;
      const coordinate = Number(tile.coordinate);
      const current = map.get(coordinate);
      if (!current || Number(tile.layer) >= Number(current.layer || 0)) {
        map.set(coordinate, {
          coordinate,
          layer: Number(tile.layer) || 0,
          interaction,
          tile
        });
      }
    });
    return map;
  }, [scene?.tiles]);

  const playerFacing = useMemo(() => {
    if (!player) return 'south';
    return String(player.facing || player.direction || 'south').trim() || 'south';
  }, [player]);

  const canMoveTo = useCallback((x, y) => {
    const width = Number(scene?.width) || 0;
    const height = Number(scene?.height) || 0;
    if (!Number.isInteger(x) || !Number.isInteger(y)) return false;
    if (x < 0 || y < 0 || x >= width || y >= height) return false;
    return !blockedSet.has(y * width + x);
  }, [blockedSet, scene?.height, scene?.width]);

  const movePlayer = useCallback((dx, dy) => {
    const currentPlayer = playerRef.current;
    if (!scene || !currentPlayer || movingRef.current) return;
    const nextX = Number(currentPlayer.x || 0) + dx;
    const nextY = Number(currentPlayer.y || 0) + dy;
    if (!canMoveTo(nextX, nextY)) {
      setMovementStatus('blocked');
      return;
    }
    const nextFacing = dx > 0 ? 'east' : dx < 0 ? 'west' : dy > 0 ? 'south' : dy < 0 ? 'north' : playerFacing;
    const startX = Number(currentPlayer.x || 0);
    const startY = Number(currentPlayer.y || 0);
    const duration = 180;
    const startTime = window.performance.now();
    movingRef.current = true;
    setMovementStatus('moving');
    setPlayerVisual({ x: startX, y: startY });

    const animate = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - ((-2 * progress + 2) ** 2) / 2;
      setPlayerVisual({
        x: startX + ((nextX - startX) * eased),
        y: startY + ((nextY - startY) * eased)
      });
      if (progress < 1) {
        moveAnimationRef.current = window.requestAnimationFrame(animate);
        return;
      }
      stopMoveAnimation();
      const nextPlayer = { ...currentPlayer, x: nextX, y: nextY, facing: nextFacing };
      playerRef.current = nextPlayer;
      setPlayer(nextPlayer);
      if (!heldDirectionRef.current) {
        setMovementStatus('ready');
      }
    };

    moveAnimationRef.current = window.requestAnimationFrame(animate);
  }, [canMoveTo, playerFacing, scene, stopMoveAnimation]);

  const nearbyInteraction = useMemo(() => {
    if (!scene || !player) return null;
    const width = Number(scene.width) || 0;
    const px = Number(player.x);
    const py = Number(player.y);
    const candidates = [
      { x: px, y: py, relation: 'center' },
      { x: px, y: py - 1, relation: 'north' },
      { x: px + 1, y: py, relation: 'east' },
      { x: px, y: py + 1, relation: 'south' },
      { x: px - 1, y: py, relation: 'west' }
    ];

    for (const candidate of candidates) {
      const { x, y, relation } = candidate;
      if (!Number.isInteger(x) || !Number.isInteger(y)) continue;
      if (x < 0 || y < 0 || x >= Number(scene.width) || y >= Number(scene.height)) continue;
      const coordinate = (y * width) + x;
      const found = interactiveTileMap.get(coordinate);
      if (found && matchesAccessFrom(found.interaction?.access_from, relation)) {
        return { ...found, relation };
      }
    }

    return null;
  }, [interactiveTileMap, player, scene]);

  const exitInterior = useCallback(async () => {
    await clearSceneState();
    navigate('/rpg');
  }, [clearSceneState, navigate]);

  const handleInteraction = useCallback(async () => {
    if (!nearbyInteraction || movingRef.current) return;
    const interaction = nearbyInteraction.interaction || {};
    const target = String(interaction.target || '').trim();
    setInteractionMessage('');

    if (target === '/rpg' || target.startsWith('/rpg/')) {
      await exitInterior();
      return;
    }

    if (target.startsWith('/city/')) {
      navigate(target);
      return;
    }

    setInteractionMessage(interaction.description || interaction.label || 'Este punto todavia no tiene una accion conectada.');
  }, [exitInterior, navigate, nearbyInteraction]);

  useEffect(() => {
    if (!heldDirectionRef.current || !player || !scene || movingRef.current) return;
    const { dx, dy } = heldDirectionRef.current;
    movePlayer(dx, dy);
  }, [movePlayer, player, scene]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;
      const key = String(event.code || event.key || '');
      if (key === 'ArrowUp' || key === 'KeyW') {
        event.preventDefault();
        heldDirectionRef.current = { dx: 0, dy: -1 };
        movePlayer(0, -1);
      } else if (key === 'ArrowDown' || key === 'KeyS') {
        event.preventDefault();
        heldDirectionRef.current = { dx: 0, dy: 1 };
        movePlayer(0, 1);
      } else if (key === 'ArrowLeft' || key === 'KeyA') {
        event.preventDefault();
        heldDirectionRef.current = { dx: -1, dy: 0 };
        movePlayer(-1, 0);
      } else if (key === 'ArrowRight' || key === 'KeyD') {
        event.preventDefault();
        heldDirectionRef.current = { dx: 1, dy: 0 };
        movePlayer(1, 0);
      } else if (key === 'KeyE') {
        if (!nearbyInteraction) return;
        event.preventDefault();
        void handleInteraction();
      }
    };

    const onKeyUp = (event) => {
      const key = String(event.code || event.key || '');
      if (
        key === 'ArrowUp' || key === 'KeyW'
        || key === 'ArrowDown' || key === 'KeyS'
        || key === 'ArrowLeft' || key === 'KeyA'
        || key === 'ArrowRight' || key === 'KeyD'
      ) {
        heldDirectionRef.current = null;
        if (!movingRef.current) {
          setMovementStatus('ready');
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [handleInteraction, movePlayer, nearbyInteraction]);

  useEffect(() => () => {
    stopMoveAnimation();
  }, [stopMoveAnimation]);

  const viewportPlayer = playerVisual || player;
  const viewport = useMemo(() => ({
    width: 13,
    height: 9,
    centerX: Number(viewportPlayer?.x || 0),
    centerY: Number(viewportPlayer?.y || 0)
  }), [scene?.width, viewportPlayer?.x, viewportPlayer?.y]);

  const canvasPlayer = useMemo(() => ({
    ...player,
    x: Number(viewportPlayer?.x ?? player?.x ?? 0),
    y: Number(viewportPlayer?.y ?? player?.y ?? 0),
    movement_state: movementStatus
  }), [movementStatus, player, viewportPlayer]);

  if (loading) {
    return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando interior RPG...</div>;
  }

  if (error && !context) {
    return (
      <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center px-6 text-center">
        <div className="space-y-4 max-w-lg">
          <p className="text-xl font-bold">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/rpg')}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 font-bold text-slate-950"
          >
            <ArrowLeft size={16} /> Volver
          </button>
        </div>
      </div>
    );
  }

  if (!context?.city || !scene || !player) {
    return (
      <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center px-6 text-center">
        <div className="space-y-4 max-w-lg">
          <p className="text-xl font-bold">{error || 'Este interior no esta listo todavia.'}</p>
          <button
            type="button"
            onClick={() => navigate('/rpg')}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-3 font-bold text-slate-950"
          >
            <ArrowLeft size={16} /> Volver
          </button>
        </div>
      </div>
    );
  }

  const playerCoords = `${player.x},${player.y}`;
  const tileSize = 64;

  return (
    <div className="min-h-screen bg-[#03050c] text-white">
      <div className="mx-auto max-w-7xl px-4 py-5 md:px-6 space-y-4">
        <header className="flex flex-col gap-4 rounded-3xl border border-cyan-400/10 bg-[#07101f] p-4 md:p-5 shadow-[0_0_60px_rgba(12,18,36,0.45)]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => void exitInterior()}
                className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.35em] text-cyan-300 hover:text-cyan-200"
              >
                <ArrowLeft size={14} /> Volver a la Cúpula
              </button>
              <div className="flex items-center gap-3">
                <Gamepad2 className="text-cyan-300" size={28} />
                <div>
                  <h1 className="text-2xl md:text-4xl font-black tracking-tight">{scene.name}</h1>
                  <p className="text-sm text-slate-300">Interior de edificio con movimiento y salida directa</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-2xl border border-slate-700 bg-black/20 px-3 py-2">
                <div className="text-slate-500 uppercase tracking-widest">Estado</div>
                <div className="font-bold text-cyan-200">{movementStatus}</div>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/20 px-3 py-2">
                <div className="text-slate-500 uppercase tracking-widest">Posicion</div>
                <div className="font-bold text-cyan-200">{playerCoords}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-300">
            <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1">Escena interior</span>
            <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1">Movimiento: WASD / flechas</span>
            <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1">Salir: puerta azul o tecla E</span>
          </div>
        </header>

        {interactionMessage && (
          <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4 text-sm text-cyan-100">
            {interactionMessage}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
          <section className="rounded-3xl border border-cyan-400/10 bg-[#07101f] p-4 overflow-hidden">
            <div className="rounded-2xl border border-slate-800 bg-[#050914] overflow-hidden max-h-[calc(100vh-240px)]">
              <div className="flex min-w-max justify-center">
              <RpgCityCanvas
                scene={scene}
                player={canvasPlayer}
                tileSize={tileSize}
                viewport={viewport}
                onReady={() => {
                  if (!movingRef.current) {
                    setMovementStatus('ready');
                  }
                }}
              />
              </div>
            </div>
          </section>

          <aside className="space-y-4">
            <section className="rounded-3xl border border-slate-800 bg-[#07101f] p-4 space-y-3">
              <h2 className="flex items-center gap-2 text-lg font-bold text-cyan-300"><DoorOpen size={18} /> Interacción</h2>
              {nearbyInteraction ? (
                <div className="space-y-3 text-sm text-slate-300">
                  <p className="font-bold text-white">{nearbyInteraction.interaction.label || 'Punto interactivo'}</p>
                  <p>{nearbyInteraction.interaction.description || 'Hay una acción disponible en este interior.'}</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void handleInteraction()}
                      className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 font-bold text-cyan-100 hover:bg-cyan-500/20"
                    >
                      {nearbyInteraction.interaction.type === 'portal' ? 'Salir' : 'Interactuar'} (E)
                    </button>
</div>
                </div>
              ) : (
                <div className="text-sm text-slate-400">
                  Acercate a la puerta azul para volver a la Cúpula.
                </div>
              )}
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
